import axios, { AxiosInstance } from 'axios';
import * as Types from './api.types'
import { ProductSupplier } from './api.types';
import { ProductsMapper } from '../../utils/ProductsMapper';
import { isNumericId } from '../../utils/slug.utils';
import logger from '../../utils/logger';

export interface ApiConfig {
    baseUrl: string;
}

export class ApiService {
    private api: AxiosInstance;
    private categoriesCache: Types.CategoryFull[] | null = null;
    private categoriesCacheTime: number = 0;
    private categoriesCacheTTL: number = 300000; // 5 minutes in milliseconds

    constructor(config: ApiConfig) {
        const baseUrl = this.getBaseUrl(config.baseUrl);

        this.api = axios.create({
            baseURL: baseUrl,
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 30000, // Increased from 10s to 30s to match backend timeout
        });

        // Request interceptor to add auth token
        this.api.interceptors.request.use(
            async (config) => {
                // Only add JWT token for auth-specific endpoints (orders, profile, etc.)
                const authToken = localStorage.getItem('authToken');
                const isAuthEndpoint = config.url?.startsWith('/auth') ||
                                      config.url?.startsWith('/orders') ||
                                      config.url?.startsWith('/slugs') ||
                                      config.url?.startsWith('/admin');

                if (authToken && isAuthEndpoint) {
                    config.headers['Authorization'] = `Bearer ${authToken}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        this.api.interceptors.response.use(
            (response) => response,
            async (error) => {
                // In production, suppress 404 errors for slug endpoints
                if (process.env.NODE_ENV === 'production' &&
                    error.response?.status === 404 &&
                    error.config?.url?.includes('/slugs/')) {
                    // Silently handle slug 404s - they're expected
                    return Promise.reject(error);
                }

                if (error.code === 'ECONNREFUSED') {
                    logger.error('Connection refused - check if backend is running and accessible');
                } else if (error.response?.status === 429 || error.response?.data?.message?.includes('Too Many Attempts')) {
                    logger.warn('Rate limit exceeded - please wait before making more requests');
                } else if (error.response?.status >= 500) {
                    logger.error('Server error:', error.response?.data?.message || error.message);
                }
                return Promise.reject(error);
            }
        );
    }

    private getBaseUrl(providedUrl: string): string {
        if (process.env.NODE_ENV === 'production') {
            return (process.env.REACT_APP_API_URL || 'https://pccenter.bg');
        }

        return (process.env.REACT_APP_DEV_API_URL || 'http://localhost:8000');
    }

    // Categories
    async getCategories(): Promise<Types.CategoryFull[]> {
        try {
            // Check if we have valid cached categories
            const now = Date.now();
            if (this.categoriesCache && (now - this.categoriesCacheTime) < this.categoriesCacheTTL) {
                logger.debug('Using browser-cached categories');
                return this.categoriesCache;
            }

            // Clear expired cache to free memory
            if (this.categoriesCache && (now - this.categoriesCacheTime) >= this.categoriesCacheTTL) {
                this.categoriesCache = null;
                logger.debug('Clearing expired categories cache');
            }

            const response = await this.api.get('/categories');
            const categories = Array.isArray(response.data) ? response.data : [];

            // Cache the categories in memory
            this.categoriesCache = categories;
            this.categoriesCacheTime = now;

            return categories;
        } catch (error) {
            logger.error('Error fetching categories:', error);
            // Return cached categories if available, even if expired
            return this.categoriesCache || [];
        }
    }

    /**
     * Every product in the category, in the order the backend ranked them.
     * Callers that filter, sort and page client-side need the whole list -
     * taking a slice of it drops whichever suppliers the backend listed last.
     */
    async getAllProductsByCategory(categoryId: number): Promise<Types.ProductFull[]> {
        try {
            const response = await this.api.get(`/products/by-category/${categoryId}`);
            const items = response.data?.data;
            return Array.isArray(items) ? items : [];
        } catch (error) {
            logger.error(`Error fetching products for category ${categoryId}:`, error);
            return [];
        }
    }

    async getProductsByCategoryFull(categoryId: number, params: Types.ProductFilterParams): Promise<Types.PagenatedProducts> {
        try {
            const response = await this.api.get(`/products/by-category/${categoryId}`);
            const items: Types.ProductFull[] = response.data?.data ?? [];

            if (Array.isArray(items)) {
                let allProducts = items;
                let filteredProducts = [...allProducts];

                if (params.manufacturer_ids) {
                    const manufacturerIds = params.manufacturer_ids.split(',').map(Number);
                    filteredProducts = filteredProducts.filter(product =>
                        manufacturerIds.includes(product.manufacturer_id)
                    );
                }

                if (params.price_min) {
                    const minPrice = parseFloat(params.price_min);
                    filteredProducts = filteredProducts.filter(product =>
                        (product.price_promo || product.price_partner) >= minPrice
                    );
                }

                if (params.price_max) {
                    const maxPrice = parseFloat(params.price_max);
                    filteredProducts = filteredProducts.filter(product =>
                        (product.price_promo || product.price_partner) <= maxPrice
                    );
                }

                if (params.in_stock === 'true') {
                    filteredProducts = filteredProducts.filter(product =>
                        product.status === 1
                    );
                }

                const perPage = params.per_page || 12;
                const currentPage = params.page || 1;
                const totalItems = filteredProducts.length;
                const lastPage = Math.ceil(totalItems / perPage);

                const startIndex = (currentPage - 1) * perPage;
                const endIndex = startIndex + perPage;
                const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

                return {
                    items: paginatedProducts,
                    total_items: totalItems,
                    per_page: perPage,
                    current_page: currentPage,
                    last_page: lastPage,
                    next_page_url: currentPage < lastPage ? `?page=${currentPage + 1}&per_page=${perPage}` : null,
                    prev_page_url: currentPage > 1 ? `?page=${currentPage - 1}&per_page=${perPage}` : null
                };
            } else {
                return response.data;
            }
        } catch (error) {
            return {
                items: [],
                total_items: 0,
                per_page: params.per_page || 12,
                current_page: params.page || 1,
                last_page: 0,
                next_page_url: null,
                prev_page_url: null
            };
        }
    }

    async getProductBySupplierFull(supplier: ProductSupplier, productId: string): Promise<Types.ProductFull | null> {
        try {
            const response = await this.api.get(`/products/${supplier}/${productId}`);
            if (!response.data?.success || !response.data?.data) return null;
            return response.data.data as Types.ProductFull;
        } catch (error) {
            logger.error(`Error fetching ${supplier} product ${productId}:`, error);
            return null;
        }
    }

    async resolveProductSlug(slug: string): Promise<{ supplier: ProductSupplier; productId: string } | null> {
        // A bare id in the URL says nothing about which catalogue it belongs to,
        // so ask the backend instead of assuming Vali.
        if (isNumericId(slug)) {
            return this.detectProductSupplier(slug);
        }

        try {
            const response = await this.api.get(`/slugs/product/${slug}`);
            return {
                supplier: response.data.supplier as ProductSupplier,
                productId: String(response.data.productId),
            };
        } catch (error) {
            logger.warn('Slug resolution failed, falling back to raw id lookup:', error);
            return this.detectProductSupplier(slug);
        }
    }

    private async detectProductSupplier(id: string): Promise<{ supplier: ProductSupplier; productId: string } | null> {
        try {
            const response = await this.api.get(`/products/${id}`);
            const product = response.data?.success ? response.data.data : null;
            if (product?.supplier) {
                return {
                    supplier: product.supplier as ProductSupplier,
                    productId: String(product.id),
                };
            }
        } catch (error) {
            logger.warn(`Supplier detection failed for product id ${id}:`, error);
        }

        return isNumericId(id) ? { supplier: 'vali', productId: id } : null;
    }

    async resolveCategorySlug(slug: string): Promise<number | null> {
        try {
            if (isNumericId(slug)) {
                return parseInt(slug, 10);
            }
            
            const response = await this.api.get(`/slugs/category/${slug}`);
            return response.data.categoryId;
        } catch (error) {
            logger.error('Error resolving category slug:', error);
            return null;
        }
    }

    async generateAllProductSlugs(): Promise<{
        total: number;
        combined: { created: number; skipped: number; errors: number };
        perSupplier: Record<ProductSupplier, { created: number; skipped: number; errors: number }>;
    }> {
        const response = await this.api.post('/slugs/admin/generate-all-products', {}, { timeout: 600000 });
        return response.data;
    }

    async generateAllCategorySlugs(): Promise<{ created: number; skipped: number; errors: number; total: number }> {
        const response = await this.api.post('/slugs/admin/generate-all-categories', undefined, { timeout: 600000 });
        return response.data;
    }

    async getCategoryDescription(categoryId: number): Promise<string | null> {
        const response = await this.api.get(`/slugs/category-text/${categoryId}`);
        return response.data.description;
    }

    async setCategoryDescription(categoryId: number, description: string): Promise<string | null> {
        const response = await this.api.put(`/slugs/category-text/${categoryId}`, { description });
        return response.data.description;
    }

    async getSlugStats(): Promise<{
        productSlugs: number;
        categorySlugs: number;
        perSupplier: Record<ProductSupplier, number>;
    }> {
        const response = await this.api.get('/slugs/admin/stats');
        return response.data;
    }

    async listCategoryMappings(filter: 'needs_review' | 'all' = 'needs_review', supplier?: 'asbis' | 'dekada'): Promise<Array<{
        id: number;
        supplier: 'asbis' | 'dekada';
        source_key: string;
        vali_category_id: number | null;
        vali_category_name: string | null;
        confidence: string | null;
        origin: string;
        product_count: number;
    }>> {
        const params = new URLSearchParams({ filter });
        if (supplier) params.set('supplier', supplier);
        const response = await this.api.get(`/admin/category-mappings?${params.toString()}`);
        return response.data.data;
    }

    async updateCategoryMapping(id: number, valiCategoryId: number | null): Promise<void> {
        await this.api.patch(`/admin/category-mappings/${id}`, { vali_category_id: valiCategoryId });
    }

    async listCatalogueDuplicates(filters: {
        supplier?: ProductSupplier;
        availability?: Types.DuplicateAvailability;
        categoryId?: number;
        search?: string;
        manualOnly?: boolean;
        limit?: number;
    } = {}): Promise<Types.CatalogueDuplicateScan> {
        const params = new URLSearchParams({ limit: String(filters.limit ?? 100) });
        if (filters.supplier) params.set('supplier', filters.supplier);
        if (filters.availability) params.set('availability', filters.availability);
        if (filters.categoryId != null) params.set('category_id', String(filters.categoryId));
        if (filters.search?.trim()) params.set('search', filters.search.trim());
        if (filters.manualOnly) params.set('manual_only', 'true');
        const response = await this.api.get(`/admin/duplicates?${params.toString()}`);
        return response.data.data;
    }

    /** Search every listing in all three catalogues, matched into a group or not. */
    async searchDuplicateCandidates(query: string, limit = 40): Promise<Types.CatalogueListing[]> {
        const params = new URLSearchParams({ q: query, limit: String(limit) });
        const response = await this.api.get(`/admin/duplicates/listings?${params.toString()}`);
        return response.data.data.items;
    }

    /** Vali products the last sync did not carry - the feed has dropped them. */
    async getStaleProductReport(limit = 20): Promise<Types.StaleProductReport> {
        const response = await this.api.get(`/admin/products/stale?limit=${limit}`);
        return response.data.data;
    }

    /** Every manual link, with the state of both listings it names. */
    async listDuplicateLinks(): Promise<Types.DuplicateLinkList> {
        const response = await this.api.get('/admin/duplicates/links');
        return response.data.data;
    }

    /** Drop every link pointing at a product that has left the feeds. */
    async pruneDuplicateLinks(): Promise<number> {
        const response = await this.api.post('/admin/duplicates/links/prune', {});
        if (!response.data?.success) {
            throw new Error(response.data?.message ?? 'Prune failed');
        }
        return response.data.data.removed;
    }

    /** Declare two listings the same product, or undo that. */
    async linkDuplicates(a: string, b: string, unlink = false): Promise<void> {
        const response = await this.api.post('/admin/duplicates/link', { a, b, unlink });
        if (!response.data?.success) {
            throw new Error(response.data?.message ?? 'Duplicate link failed');
        }
    }

    async decideDuplicate(
        groupKey: string,
        mode: Types.DuplicateMode,
        preferredIds?: string[],
    ): Promise<void> {
        const response = await this.api.post('/admin/duplicates/decision', {
            group_key: groupKey,
            mode,
            preferred_ids: preferredIds,
        });
        if (!response.data?.success) {
            throw new Error(response.data?.message ?? 'Duplicate decision failed');
        }
    }

    async listValiCategoriesForDropdown(): Promise<Array<{ id: number; name: string }>> {
        const response = await this.api.get('/admin/vali-categories');
        return response.data.data;
    }

    // Search products using backend full-text search
    async searchProducts(searchText: string, page: number = 1, perPage: number = 50): Promise<Types.ProductFull[]> {
        try {
            const searchQuery = searchText.trim();
            if (!searchQuery || searchQuery.length < 2) return [];

            logger.log(`Searching for: "${searchText}"`);

            // Call the new backend search endpoint
            const response = await this.api.get('/search', {
                params: {
                    q: searchQuery,
                    page: page,
                    per_page: perPage
                }
            });

            if (response.data.success && response.data.data) {
                const items = response.data.data.items;
                
                // Filter out any products containing "Vali" in name or manufacturer
                // Also filter out INKTEC oil bottles and UPRINT ink cartridges unless searching for them
                const searchingForOil = searchQuery.toLowerCase().includes('масло') || searchQuery.toLowerCase().includes('oil');
                const searchingForInk = searchQuery.toLowerCase().includes('мастилница') || 
                                       searchQuery.toLowerCase().includes('мастило') || 
                                       searchQuery.toLowerCase().includes('ink') || 
                                       searchQuery.toLowerCase().includes('cartridge');
                
                const filteredItems = items.filter((item: any) => {
                    const nameContainsVali = item.name && item.name.toLowerCase().includes('vali');
                    const manufacturerContainsVali = item.manufacturer && item.manufacturer.toLowerCase().includes('vali');
                    
                    // Check for INKTEC oil bottles
                    const isInktecOil = (item.name && item.name.toLowerCase().includes('inktec') && 
                                        item.name.toLowerCase().includes('масло')) ||
                                       (item.manufacturer && item.manufacturer.toLowerCase().includes('inktec') && 
                                        item.name && item.name.toLowerCase().includes('масло'));
                    
                    // Check for UPRINT ink cartridges
                    const isUprintInk = (item.name && item.name.toLowerCase().includes('uprint') && 
                                        item.name.toLowerCase().includes('мастилница')) ||
                                       (item.manufacturer && item.manufacturer.toLowerCase().includes('uprint') && 
                                        item.name && item.name.toLowerCase().includes('мастилница'));
                    
                    // Exclude Vali products
                    if (nameContainsVali || manufacturerContainsVali) return false;
                    
                    // Exclude INKTEC oil unless searching for oil
                    if (!searchingForOil && isInktecOil) return false;
                    
                    // Exclude UPRINT ink unless searching for ink
                    if (!searchingForInk && isUprintInk) return false;
                    
                    return true;
                });
                
                // Transform backend response to match ProductFull interface
                return filteredItems.map((item: any) => ({
                    id: item.id,
                    supplier: item.supplier,
                    name: [{ language_code: 'bg', text: item.name }],
                    description: item.description ? [{ language_code: 'bg', text: item.description }] : [],
                    reference_number: item.reference_number,
                    model: item.model,
                    manufacturer_id: item.manufacturer_id,
                    manufacturer: item.manufacturer,
                    price_partner: typeof item.price_partner === 'string' ? parseFloat(item.price_partner) : item.price_partner,
                    price_promo: item.price_promo ? (typeof item.price_promo === 'string' ? parseFloat(item.price_promo) : item.price_promo) : null,
                    status: item.status,
                    images: item.images || [],
                    categories: item.categories || [],
                    attributes: item.attributes || []
                }));
            }
            
            return [];
        } catch (error) {
            logger.error('Search error:', error);
            // Fallback to empty results on error
            return [];
        }
    }
    
    // Orders
    async createOrder(orderData: Types.CreateOrderPayload): Promise<Types.Order> {
        const response = await this.api.post('/orders', orderData);
        return response.data;
    }

    async getOrders(params: Types.PaginationParams & Types.OrderDateFilter): Promise<Types.PagenatedOrders> {
        const response = await this.api.get('/orders', { params });
        return response.data;
    }

    // Cleanup method to free memory and prevent leaks
    public cleanup(): void {
        // Clear all caches
        this.categoriesCache = null;
        this.categoriesCacheTime = 0;

        logger.debug('ApiService caches and queues cleared');
    }
}